import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from "react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import SimpleOfflineModal from "./SimpleOfflineModal";
import OfflineModal from "./OfflineModal";

interface OfflineContextType {
    isOnline: boolean;
    isOffline: boolean;
    showOfflineModal: boolean;
    setShowOfflineModal: (show: boolean) => void;
    retryConnection: () => Promise<boolean>;
    hasBeenOffline: boolean;
    lastOnlineTime: Date | null;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

interface OfflineProviderProps {
    children: ReactNode;
    autoShowModal?: boolean;
    modalDelay?: number;
    enableAutoRetry?: boolean;
    retryInterval?: number;
    useSimpleModal?: boolean; // New option to choose modal type
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({
    children,
    autoShowModal = true,
    modalDelay = 3000, // Show modal after 3 seconds offline
    enableAutoRetry = true,
    retryInterval = 10000, // Auto retry every 10 seconds
    useSimpleModal = true, // Default to simple modal
}) => {
    const {
        isOnline,
        isOffline,
        hasBeenOffline,
        lastOnlineTime,
        retryConnection,
    } = useOnlineStatus();

    const [showOfflineModal, setShowOfflineModal] = useState(false);
    const [offlineStartTime, setOfflineStartTime] = useState<Date | null>(null);

    // Track when we go offline
    useEffect(() => {
        if (isOffline && !offlineStartTime) {
            setOfflineStartTime(new Date());
        } else if (isOnline && offlineStartTime) {
            setOfflineStartTime(null);
            setShowOfflineModal(false);
        }
    }, [isOnline, isOffline, offlineStartTime]);

    // Auto show modal after delay
    useEffect(() => {
        if (!autoShowModal || !offlineStartTime || showOfflineModal) return;

        const timer = setTimeout(() => {
            if (isOffline) {
                setShowOfflineModal(true);
            }
        }, modalDelay);

        return () => clearTimeout(timer);
    }, [
        autoShowModal,
        offlineStartTime,
        modalDelay,
        isOffline,
        showOfflineModal,
    ]);

    // Handle failed network requests globally
    useEffect(() => {
        const handleFetchError = (event: any) => {
            // Check if this is a network error
            if (
                event.reason &&
                (event.reason.message?.includes("Failed to fetch") ||
                    event.reason.message?.includes("Network Error") ||
                    event.reason.name === "TypeError")
            ) {
                console.warn("Network request failed, checking connection...");
                retryConnection();
            }
        };

        window.addEventListener("unhandledrejection", handleFetchError);
        return () =>
            window.removeEventListener("unhandledrejection", handleFetchError);
    }, [retryConnection]);

    // Intercept fetch requests to handle offline scenarios
    useEffect(() => {
        if (typeof window === "undefined") return;

        const originalFetch = window.fetch;

        window.fetch = async (...args) => {
            try {
                const response = await originalFetch(...args);

                // If request succeeds but we thought we were offline, update status
                if (!isOnline && response.ok) {
                    retryConnection();
                }

                return response;
            } catch (error) {
                // If request fails, check if we're actually offline
                if (!isOnline) {
                    // Already offline, just throw the error
                    throw error;
                }

                // Check if this is a network error
                if (
                    error instanceof TypeError ||
                    (error as any).message?.includes("Failed to fetch")
                ) {
                    // Fetch failed, checking connection status - console logging removed
                    const connectionOk = await retryConnection();

                    if (!connectionOk) {
                        // We're actually offline, show modal if not already shown
                        if (autoShowModal && !showOfflineModal) {
                            setShowOfflineModal(true);
                        }
                    }
                }

                throw error;
            }
        };

        return () => {
            window.fetch = originalFetch;
        };
    }, [isOnline, retryConnection, autoShowModal, showOfflineModal]);

    const contextValue: OfflineContextType = {
        isOnline,
        isOffline,
        showOfflineModal,
        setShowOfflineModal,
        retryConnection,
        hasBeenOffline,
        lastOnlineTime,
    };

    return (
        <OfflineContext.Provider value={contextValue}>
            {children}

            {useSimpleModal ? (
                <SimpleOfflineModal
                    isOpen={showOfflineModal}
                    onClose={() => setShowOfflineModal(false)}
                    onRetry={retryConnection}
                />
            ) : (
                <OfflineModal
                    isOpen={showOfflineModal}
                    onClose={() => setShowOfflineModal(false)}
                    onRetry={retryConnection}
                    autoRetry={enableAutoRetry}
                    retryInterval={retryInterval}
                    title="Koneksi Terputus"
                    message="Sepertinya Anda tidak terhubung ke internet saat ini. Beberapa fitur dan konten terbaru mungkin tidak tersedia."
                />
            )}
        </OfflineContext.Provider>
    );
};

export const useOffline = (): OfflineContextType => {
    const context = useContext(OfflineContext);
    if (context === undefined) {
        throw new Error("useOffline must be used within an OfflineProvider");
    }
    return context;
};

export default OfflineProvider;
